import { useEffect, useMemo, useState } from 'react'
import {
  MRT_PaginationState,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_ColumnFiltersState,
} from 'mantine-react-table'
import { ContactListItem } from '@/shared/api/topli/TopliApi'
import { useContactsByPage } from '@/entities/Contact'
import { convertPhoneNumberToString, findLabelByValue } from '@/shared/lib/utils'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/shared/lib/useDebounce'
import { Checkbox } from '@mantine/core'
import { useLocation, useSearchParams } from 'react-router-dom'
import { digitsOnlyFilterProps } from '@/shared/lib/tableFilterUtils'
import { useTableFilters } from '@/shared/hooks/useTableFilters'

export const useContactsTable = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const contactId = searchParams.get('id')

  const [search, setSearch] = useState(location?.state?.name || '')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const { titleOptions } = useTranslatedEnums()
  const { t, i18n } = useTranslation(['common', 'contact'])
  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() => {
    return contactId ? [{ id: 'id', value: contactId }] : []
  })

  const { getTextFilter } = useTableFilters(columnFilters)

  useEffect(() => {
    const idFilter = getTextFilter('id')
    if (!idFilter && searchParams.has('id')) {
      setSearchParams({})
    }
  }, [columnFilters])

  const debouncedSearch = useDebounce(search, 500)
  const debouncedSearchById = useDebounce(getTextFilter('id') || '', 500)

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isError: isErrorContacts,
    isFetching: isFetchingContacts,
  } = useContactsByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    debouncedSearch,
    debouncedSearchById,
  )

  const fetchedContacts = contacts?.data ?? []
  const totalRowCount = contacts?.total ?? 0

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  useEffect(() => {
    if (location.state?.name) {
      setSearch(location.state.name || '')
    }
  }, [location.state])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<ContactListItem>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        filterVariant: 'text',
        size: 90,
        ...digitsOnlyFilterProps(),
      },
      {
        accessorKey: 'fullname',
        enableColumnFilter: false,
        header: t('contact:full_name'),
        Cell: ({ row }) => <div className='text-sm font-semibold'>{row.original.fullname}</div>,
      },
      {
        accessorKey: 'title',
        enableColumnFilter: false,
        header: t('common:library_items.title.sing'),
        Cell: ({ row }) => (
          <div className='text-sm'>{findLabelByValue(titleOptions, row.original.title ?? 4)}</div>
        ),
      },
      {
        accessorKey: 'email',
        enableColumnFilter: false,
        header: t('contact:email'),
      },
      {
        accessorKey: 'phone',
        enableColumnFilter: false,
        header: t('contact:phone'),
        enableSorting: false,
        Cell: ({ row }) => (
          <div className='text-sm'>{convertPhoneNumberToString(row.original.phone)}</div>
        ),
      },
      {
        accessorKey: 'phone2',
        enableColumnFilter: false,
        header: t('contact:second_phone'),
        Cell: ({ row }) => (
          <div className='text-sm'>{convertPhoneNumberToString(row.original.phone2 ?? null)}</div>
        ),
      },
      {
        accessorKey: 'is_man',
        enableColumnFilter: false,
        header: t('common:library_items.gender.sing'),
        Cell: ({ row }) => (
          <div className='text-sm'>
            {row.original.is_man
              ? t('common:library_items.gender.items.male')
              : t('common:library_items.gender.items.female')}
          </div>
        ),
      },
      {
        accessorKey: 'is_client',
        enableColumnFilter: false,
        header: t('contact:client'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.is_client} readOnly />,
      },
    ],
    [i18n.language],
  )
  return {
    search,
    pagination,
    fetchedContacts,
    totalRowCount,
    columns,
    isErrorContacts,
    isFetchingContacts,
    isLoadingContacts,
    sorting,
    setSorting,
    setPagination,
    handleSearchChange,
    columnFilters,
    setColumnFilters,
  }
}
